import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { PWAUtils } from "../utils/PWAUtils";
import AlertModal from "../components/Modals/AlertModal";
import InstagramModal from "../components/Modals/InstagramModal";

// Capture beforeinstallprompt at module level so it's never missed
let earlyDeferredPrompt: any = null;
window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    earlyDeferredPrompt = e;
});

interface PWAActions {
    installPWA: () => void;
    isIos: () => boolean;
    isStandalone: () => boolean;
    isAppleDesktop: () => boolean;
    isInstagram: () => boolean;
    deviceFormFactor: () => "narrow" | "wide";
}

const PWAContext = createContext<PWAActions | undefined>(undefined);

export const PWAProvider: React.FC<{
    appInitialized: boolean;
    children: React.ReactNode;
}> = ({ appInitialized, children }) => {
    const deferredPromptRef = useRef<any>(earlyDeferredPrompt);
    const [isAlertModalOpen, setAlertModalOpen] = useState(false);
    const [isInstagramModalOpen, setInstagramModalOpen] = useState(false);
    const [alertModalText, setAlertModalText] = useState<string | undefined>();

    useEffect(() => {
        // Listen for future beforeinstallprompt events (e.g. after user dismisses)
        const handleBeforeInstallPrompt = (e: any) => {
            e.preventDefault();
            deferredPromptRef.current = e;
        };

        window.addEventListener(
            "beforeinstallprompt",
            handleBeforeInstallPrompt
        );

        return () => {
            window.removeEventListener(
                "beforeinstallprompt",
                handleBeforeInstallPrompt
            );
        };
    }, []);

    useEffect(() => {
        // Check for Instagram browser on app load
        if (isInstagram()) setInstagramModalOpen(true);
    }, []);

    const installPWA = () => {
        if (isStandalone()) {
            setAlertModalText("The app is already installed!");
            setAlertModalOpen(true);
            return;
        } else if (isInstagram()) {
            setAlertModalText(
                "Instagram's browser can't install apps. Tap ··· in the top right and choose \"Open in Safari\" or \"Open in Chrome\", then try again."
            );
            setAlertModalOpen(true);
            return;
        } else if (isIos()) {
            window.location.href = "/install-ios";
            return;
        } else if (isAppleDesktop()) {
            setAlertModalText(
                "To install this app on your macOS device, click the share button in Safari and select 'Add to Dock'."
            );
            setAlertModalOpen(true);
            return;
        } else if (deferredPromptRef.current) {
            const prompt = deferredPromptRef.current;
            deferredPromptRef.current = null;
            prompt.prompt();
            prompt.userChoice.finally(() => {});
            return;
        } else {
            setAlertModalText(
                "Installation is not supported in this browser. If you're using iPhone open the app in Safari."
            );
            setAlertModalOpen(true);
            return;
        }
    };

    const isIos = () => PWAUtils.isAppleMobile();
    const isStandalone = () => PWAUtils.isStandalone();
    const isAppleDesktop = () => PWAUtils.isAppleDesktop();
    const deviceFormFactor = () => PWAUtils.deviceFormFactor();
    const isInstagram = () => PWAUtils.isInstagram();

    return (
        <PWAContext.Provider
            value={{
                installPWA,
                isIos,
                isStandalone,
                isAppleDesktop,
                isInstagram,
                deviceFormFactor,
            }}
        >
            {children}
            {alertModalText && (
                <AlertModal
                    open={isAlertModalOpen}
                    onClose={() => setAlertModalOpen(false)}
                    alertText={alertModalText}
                />
            )}
            <InstagramModal
                open={isInstagramModalOpen}
                onClose={() => setInstagramModalOpen(false)}
                alertText={""} // Whatever
            />
        </PWAContext.Provider>
    );
};

export const usePWA = (): PWAActions => {
    const context = useContext(PWAContext);
    if (!context) {
        throw new Error("usePWA must be used within a PWAProvider");
    }
    return context;
};
